import CustomTextField from 'components/Input/CustomTextField';
import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import { Grid, IconButton, Typography } from '@material-ui/core';
import UploadFileInput from 'components/uploadFile/uploadFileInput';
import { makeStyles } from '@material-ui/core/styles';
import CustomButton from 'components/CustomInput/Button';
import TextHeader from 'components/CustomText/TextHeader';
import { Delete as DeleteIcon } from '@material-ui/icons';
import UploadDisplay from 'components/uploadFile/UploadDisplay';
import API from 'api/call';
import { generateRandomId, toHalfWidthUnicode } from 'utility/general';

const useStyles = makeStyles((theme) => ({
    titleBox: {
        marginBottom: 4,
    },
    limitText: {
        margin: '0 16px 0 8px',
    },
    itemBox: {
        marginTop: theme.spacing(1),
    },
    itemInput: {
        marginRight: theme.spacing(2),
    },
    fileBox: {
        marginTop: theme.spacing(1),
    },
}));

const GetContent = (props) => {
    const {
        item,
        uploadData,
        index,
        handleFormData,
        fileOnChange,
        handleDelete,
        forceErrorDisplay,
        enableRemove,
        onRemove,
        isLoading,
    } = props;

    const location = useLocation();
    const isAmendmentForm = location?.pathname?.includes('application-amendment-detail');

    let html = null;
    switch (item.type) {
        case 'input':
            html = (
                <CustomTextField
                    label={item.label}
                    name={item.name}
                    value={item.value}
                    type="input"
                    onChange={(el) => handleFormData(el, index)}
                    validators={item.validators}
                    forceErrorDisplay={forceErrorDisplay}
                    isNoSpace
                    maxLength={item.maxLength}
                />
            );
            break;
        case 'date':
            html = (
                <CustomDatePicker
                    name={item.name}
                    value={item.value}
                    label={item.label}
                    handleChangeDate={(data) =>
                        handleFormData({ ...data, target: { ...data.target, type: 'date' } }, index)
                    }
                    validators={item.validators}
                    forceErrorDisplay={forceErrorDisplay}
                />
            );
            break;
        case 'upload':
            html = (
                <Grid container wrap="nowrap" justify="space-between" alignItems="center">
                    <Grid container item xs={10} alignItems="flex-start" wrap="nowrap">
                        <Grid style={{ whiteSpace: 'nowrap', paddingTop: 10 }}>Upload Document:</Grid>
                        <Grid container style={{ margin: '0 8px', maxHeight: 140, overflowY: 'auto', width: '100%' }}>
                            {uploadData.uploadTempId && !isLoading ? (
                                <Grid container>
                                    <UploadDisplay
                                        fileHeight={38}
                                        enableRemove={enableRemove}
                                        onRemove={(idx) => onRemove(index, idx)}
                                        fileList={uploadData.value}
                                        enableDownload={true}
                                        downloadType={uploadData.downloadType}
                                        isLocalDownload
                                    />
                                </Grid>
                            ) : (
                                <Grid
                                    container
                                    style={{
                                        color: uploadData.error ? 'red' : 'unset',
                                        marginBottom: 8,
                                        paddingTop: 10,
                                    }}
                                >
                                    No file has uploaded!
                                </Grid>
                            )}
                        </Grid>
                        {isAmendmentForm && (
                            <Typography color="textSecondary" style={{ marginTop: 4, color: '#FF0000' }}>
                                * Clean and track version of documents are required to upload (except newly-submitted
                                document) for Amendment Application
                            </Typography>
                        )}
                    </Grid>
                    <Grid container wrap="nowrap" item xs={2}>
                        <UploadFileInput
                            isLoading={isLoading}
                            fileOnChange={(list) => fileOnChange(list, index)}
                            disabled={uploadData.value.length > 0}
                        />
                        <IconButton onClick={() => handleDelete(index)} size="small">
                            <DeleteIcon color="primary" />
                        </IconButton>
                    </Grid>
                </Grid>
            );
            break;
        default:
            html = (
                <Grid container wrap="nowrap" alignItems="center">
                    {item.value ? item.value : null}
                </Grid>
            );
            break;
    }
    return html;
};

const UploadModal = (props) => {
    const classes = useStyles();

    const {
        groupType,
        title,
        formData,
        setFormData,
        defaultFormData = {
            date: {
                id: '0',
                value: '',
                name: 'date',
                label: 'Date',
                type: 'date',
            },
            upload: {
                id: '1',
                value: [],
                type: 'upload',
            },
        },
        forceErrorDisplay,
        enableRemove = false,
        onRemove = (index) => null,
    } = props;

    const [loadingList, setLoadingList] = useState({});

    const deletedIds = useRef([]);

    const fileOnChange = (list, index) => {
        const copyFormData = JSON.parse(JSON.stringify(formData));
        copyFormData.forEach((item, index) => {
            if (formData[index].upload.value[0]?.file) {
                item.upload.value[0].file = formData[index].upload.value[0].file;
            }
        });

        if (list.length) {
            const tempId = copyFormData[index].upload.id;
            setLoadingList({ ...loadingList, [tempId]: true });

            let requestFormData = new FormData();

            let file = list[0];

            try {
                // convert the file name to all half width unicode for fix of code scan issue
                file = new File([list[0]], toHalfWidthUnicode(list[0].name), {
                    type: list[0].type,
                });
            } catch (error) {
                console.log('%c 121528 UploadModal.jsx error', 'background: green', error);
            }

            requestFormData.append('file', file);

            API.file
                .uploadDocument(requestFormData)
                .then((res) => {
                    const id = res.data?.result?.result[0].key;
                    copyFormData[index].upload.value = list.map((item) => {
                        return { name: item.name, downloadId: id, file: list[0] };
                    });
                    /** Judgment logic for client delete the file uploading row */
                    if (!deletedIds.current.includes(copyFormData[index].upload.id)) {
                        copyFormData[index].upload.uploadTempId = id;
                        setFormData(copyFormData);
                    }
                })
                .finally(() => {
                    setLoadingList({ ...loadingList, [tempId]: false });
                });
        }
    };

    const handleFormData = (el, index) => {
        const { value, name } = el.target;
        const copyFormData = [...formData];
        copyFormData[index][name].value = value;
        setFormData(copyFormData);
    };
    const handleAdd = () => {
        const copyFormData = [...formData];
        let data = JSON.parse(JSON.stringify(defaultFormData));
        data.upload.id = 'temp_' + generateRandomId();

        copyFormData.push(data);
        setFormData(copyFormData);
    };
    const handleDelete = (index) => {
        const copyFormData = [...formData];
        deletedIds.current.push(formData[index]?.upload.id);
        copyFormData.splice(index, 1);

        setFormData(copyFormData);
    };

    return (
        <Grid container alignItems="flex-start">
            <Grid container wrap="nowrap" alignItems="center" className={classes.titleBox}>
                <TextHeader>{title}</TextHeader>
                <Grid className={classes.limitText}>( File size limit is 50MB )</Grid>
                <CustomButton onClick={handleAdd} disabled={formData.length >= 10}>
                    ADD
                </CustomButton>
            </Grid>
            {formData.length > 0 &&
                formData.map((item, index) => (
                    <Grid container key={index}>
                        <Grid className={classes.itemBox} container wrap="nowrap" justify="space-between">
                            {Object.keys(item).map((n, idx) => (
                                <Grid
                                    container
                                    item
                                    xs={item[n].itemXs ? item[n].itemXs : item[n].type === 'upload' ? 9 : 3}
                                    key={index + idx}
                                    className={classes.itemInput}
                                >
                                    <GetContent
                                        classes={classes}
                                        item={item[n]}
                                        index={index}
                                        handleFormData={handleFormData}
                                        fileOnChange={fileOnChange}
                                        handleDelete={handleDelete}
                                        dataLength={formData.length}
                                        uploadData={item.upload}
                                        groupType={groupType}
                                        forceErrorDisplay={forceErrorDisplay}
                                        enableRemove={enableRemove}
                                        onRemove={onRemove}
                                        isLoading={loadingList[item.upload.id]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                ))}
        </Grid>
    );
};

export default UploadModal;
